import { inject, Injectable, isDevMode } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import * as signalR from "@microsoft/signalr";
import * as signalRMessagePack from "@microsoft/signalr-protocol-msgpack";
import { ConfigurationService, ConnectivityService, ToastService } from "@ramudden/services";
import { AuthenticationService } from "../authentication.service";
import { RealtimeConnection } from "./realtime-connection";

// Take care of the technicalities of setting up a SignalR connection
@Injectable({
    providedIn: "root",
})
export class SignalRService {
    private readonly authenticationService = inject(AuthenticationService);
    private readonly configurationService = inject(ConfigurationService);
    private readonly connectivityService = inject(ConnectivityService);
    private readonly toastService = inject(ToastService);
    private readonly msalService = inject(MsalService);

    public CreateConnection(contextId: number, endpoint: string): RealtimeConnection {
        const url = `${this.configurationService.configuration.url}${endpoint}`;

        const connectionOptions = {
            accessTokenFactory: () => {
                if (this.authenticationService.e2eTestToken) {
                    return Promise.resolve(this.authenticationService.e2eTestToken);
                }

                return this.msalService
                    .initialize()
                    .toPromise()
                    .then((_result) =>
                        this.authenticationService
                            .acquireTokenSilent$()
                            .toPromise()
                            .then((result) => result.accessToken),
                    );
            },
        } as signalR.IHttpConnectionOptions;

        const hubConnection = new signalR.HubConnectionBuilder()
            .configureLogging(!isDevMode() ? signalR.LogLevel.None : signalR.LogLevel.Information)
            .withHubProtocol(new signalRMessagePack.MessagePackHubProtocol())
            .withAutomaticReconnect()
            .withUrl(url, connectionOptions)
            .build();

        const connection = new RealtimeConnection(contextId, hubConnection);
        return connection;
    }

    public startConnection(connection: RealtimeConnection) {
        if (connection.closedPermanently || connection.connection.state !== signalR.HubConnectionState.Disconnected) {
            return;
        }

        const clearNoConnectionErrors = () => {
            const toBeCleanedUp = this.toastService
                .allActiveToasts()
                .filter((x) => x.message === this.connectivityService.getNoConnectionErrorMessage());
            for (const toast of toBeCleanedUp) {
                this.toastService.clearError(toast.toastId);
            }
        };

        const onSuccess = async () => {
            let result;
            if (connection.contextId !== null && connection.contextId !== undefined) {
                result = await connection.connection.invoke("subscribe", connection.contextId);
            } else {
                result = await connection.connection.invoke("subscribe");
            }

            clearNoConnectionErrors();

            // Without messagePack: 200
            // With: "OK"
            // Let's just prepare for both
            if (result !== 200 && result !== "OK") {
                if (result === 401 || result === "Unauthorized") {
                    // there can be a next situation
                    // 1. SignalR ws connection established
                    // 2. Token expired
                    // 3. Connection lost
                    // 4. Reconnection start but all the time 401 will returned
                    this.authenticationService.login(); // we simply reload current location to refresh everything
                }

                this.stopConnection(connection);
            } else {
                connection.onDetachEvents(connection);
                connection.onRegisterEvents(connection);
            }
        };

        const onReconnect = async () => {
            clearNoConnectionErrors();
            onSuccess();
        };

        const onError = () => {
            if (connection.closedPermanently) {
                return;
            }

            setTimeout(() => {
                this.startConnection(connection);
            }, 6000);
        };

        if (!connection.lifecycleHandlersRegistered) {
            connection.lifecycleHandlersRegistered = true;

            connection.connection.onreconnecting(async (error: any) => {
                const isOnline = await this.connectivityService.checkForConnection();
                if (isOnline) {
                    return;
                }

                this.connectivityService.reportMissingConnection(error);
            });
            connection.connection.onreconnected(onReconnect);
            connection.connection.onclose(onError);
        }

        connection.connection.start().then(onSuccess, onError).catch(onError);
    }

    /**
     * Stops for good, whatever state the hub is in. A connection caught mid-reconnect or with a retry pending used
     * to slip through and keep reconnecting with nobody listening; `closedPermanently` now also cancels that retry.
     */
    public stopConnection(connection: RealtimeConnection) {
        connection.closedPermanently = true;
        connection.onDetachEvents?.(connection);

        if (connection.connection.state === signalR.HubConnectionState.Disconnected) {
            return;
        }

        void connection.connection.stop();
    }
}
